import React from 'react';

import Icon, { IconProp } from '~components/icon';

interface ComponentProps {
  icon: IconProp;
  label: string; // accessible name + tooltip (e.g. "Bold")
  onActivate: () => void;
  /**
   * A second glyph drawn beside `icon`, for a control whose action needs a modifier the base glyph
   * cannot carry: the row and column buttons pair an axis glyph with a plus or a minus.
   *
   * Drawn ALONGSIDE rather than layered on top. FontAwesome's free set has no directional plus
   * (table-cells-row-plus is Pro only), and layering a minus onto faGripLines makes it disappear,
   * because a minus is a horizontal line and the grip is horizontal lines. Verified by rendering:
   * the layered version left Delete row with no visible minus at all.
   *
   * OMIT for a plain single-glyph button, which is every other control in this toolbar.
   */
  pairedIcon?: IconProp;
  /**
   * Toggle state. OMIT for an action button (insert table, add row): the button then renders no
   * aria-pressed, because a one-shot action is not a toggle and a permanent aria-pressed="false"
   * would misreport it to a screen reader.
   */
  isActive?: boolean;
  disabled?: boolean;
}

/**
 * One formatting control in the rich-text editor toolbar.
 *
 * `onMouseDown` is prevented so clicking a button never steals focus from the editor: the text
 * selection survives, so toggling Bold etc. applies to what the author had selected.
 */
const RichTextToolbarButton: React.FC<ComponentProps> = ({
  icon,
  label,
  onActivate,
  pairedIcon,
  isActive,
  disabled = false,
}) => (
  <button
    type="button"
    className={`rich-text-toolbar-btn${isActive ? ' is-active' : ''}`}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onActivate}
    disabled={disabled}
    aria-label={label}
    aria-pressed={isActive} // undefined => attribute omitted
    title={label}
  >
    {pairedIcon ? (
      <span className="rich-text-toolbar-btn-pair">
        <Icon icon={icon} />
        <Icon icon={pairedIcon} size="xs" />
      </span>
    ) : (
      <Icon icon={icon} />
    )}
  </button>
);

export default RichTextToolbarButton;
